import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import {
  cancelMatchQueue,
  confirmMatchQueueWait,
  getMatchWaitUiState,
  getPendingMatchRequest,
  resumeMatchWaitUi,
  subscribeMatchWaitUi,
} from '../../socket';

function isStandaloneTableWindow() {
  try {
    const pathname = String(window.location?.pathname || '');
    const search = new URLSearchParams(String(window.location?.search || ''));
    const popupTableId = String(search.get('popupTable') || '').trim();

    return (
      Boolean(popupTableId) ||
      pathname === '/popup' ||
      pathname.startsWith('/popup/') ||
      pathname.startsWith('/table/')
    );
  } catch (_) {
    return false;
  }
}

function formatRemaining(ms) {
  const totalSeconds = Math.max(0, Math.ceil(Number(ms || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const ESTIMATE_BATCH_LOW_MS = Math.max(100, Number(process.env.REACT_APP_MATCH_ESTIMATE_BATCH_LOW_MS || 300));
const ESTIMATE_BATCH_HIGH_MS = Math.max(ESTIMATE_BATCH_LOW_MS, Number(process.env.REACT_APP_MATCH_ESTIMATE_BATCH_HIGH_MS || 900));
const ESTIMATE_ADMISSION_LOW_MS = Math.max(300, Number(process.env.REACT_APP_MATCH_ESTIMATE_ADMISSION_LOW_MS || 1200));
const ESTIMATE_ADMISSION_HIGH_MS = Math.max(ESTIMATE_ADMISSION_LOW_MS, Number(process.env.REACT_APP_MATCH_ESTIMATE_ADMISSION_HIGH_MS || 4500));

function calculateEstimatedWait(model = {}, remainingMs = 0) {
  const sampledAt = Number(model?.queue_sampled_at || 0);
  if (!sampledAt) return null;

  const queueAhead = Math.max(0, Number(model?.queue_ahead_estimate || 0));
  const processingCount = Math.max(0, Number(model?.processing_count || 0));
  const delayedCount = Math.max(0, Number(model?.delayed_count || 0));
  const workerConcurrency = Math.max(1, Number(model?.worker_concurrency || 1));
  const effectiveAhead = queueAhead + Math.min(processingCount, workerConcurrency) + delayedCount;
  const batches = Math.max(0, Math.ceil(effectiveAhead / workerConcurrency));
  const cap = Math.max(0, Number(remainingMs || 0));
  if (cap <= 0) return null;

  let lowMs = ESTIMATE_ADMISSION_LOW_MS + batches * ESTIMATE_BATCH_LOW_MS;
  let highMs = ESTIMATE_ADMISSION_HIGH_MS + batches * ESTIMATE_BATCH_HIGH_MS;
  lowMs = Math.min(cap, Math.max(1000, lowMs));
  highMs = Math.min(cap, Math.max(lowMs, highMs));

  return { lowMs, highMs };
}

export default function MatchQueueWaitOverlay() {
  const { t } = useI18n();
  const standaloneTableWindow = useMemo(() => isStandaloneTableWindow(), []);
  const [model, setModel] = useState(() => (
    standaloneTableWindow ? null : getMatchWaitUiState()
  ));
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (standaloneTableWindow) return undefined;

    const unsubscribe = subscribeMatchWaitUi((next) => {
      setModel(next);
      setError('');
    });
    const pending = getPendingMatchRequest();
    const state = String(pending?.state || '').toLowerCase();
    if (pending?.request_id && !['matched', 'failed', 'cancelled', 'expired'].includes(state)) {
      resumeMatchWaitUi(pending);
    }
    return unsubscribe;
  }, [standaloneTableWindow]);

  useEffect(() => {
    if (!model?.open || model?.phase !== 'waiting') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [model?.open, model?.phase]);

  const remainingMs = Math.max(0, Number(model?.expires_at || model?.deadline_at || 0) - now);
  const remainingText = useMemo(
    () => formatRemaining(remainingMs),
    [remainingMs]
  );
  const estimatedWait = useMemo(
    () => calculateEstimatedWait(model, remainingMs),
    [model, remainingMs]
  );

  if (standaloneTableWindow || !model?.open || !model?.request_id) return null;

  const handleContinue = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    const result = await confirmMatchQueueWait({
      request_id: model.request_id,
      user_code: model.user_code,
    }).catch((e) => ({ success: false, error: e?.message || 'confirm_wait_failed' }));
    if (!result?.success) setError(t('matchQueue.error.confirmFailed', null, '대기 상태 확인에 실패했습니다. 다시 선택해주세요.'));
    setBusy(false);
  };

  const handleCancel = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    const result = await cancelMatchQueue({
      request_id: model.request_id,
      user_code: model.user_code,
      reason: 'user_cancelled_long_wait',
    }).catch((e) => ({ success: false, error: e?.message || 'cancel_match_failed' }));
    if (!result?.success && !result?.cancelled) {
      setError(t('matchQueue.error.cancelFailed', null, '매칭 취소에 실패했습니다. 잠시 후 다시 시도해주세요.'));
      setBusy(false);
    }
  };

  const waiting = model.phase === 'waiting';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('matchQueue.dialogAria', null, '매칭 대기 안내')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: 'rgba(2, 5, 12, 0.76)',
        backdropFilter: 'blur(5px)',
      }}
    >
      <div style={{
        width: 'min(420px, calc(100vw - 36px))',
        borderRadius: 18,
        border: '1px solid rgba(104, 213, 255, 0.48)',
        background: 'linear-gradient(180deg, rgba(15, 25, 41, 0.98), rgba(5, 10, 20, 0.98))',
        boxShadow: '0 24px 70px rgba(0,0,0,.58), 0 0 28px rgba(58,190,255,.16)',
        color: '#f7fbff',
        padding: '24px 22px 20px',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.12em', color: '#70dcff', marginBottom: 10 }}>
          {t('matchQueue.kicker', null, 'MATCHING QUEUE')}
        </div>

        {waiting ? (
          <>
            <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 10 }}>{t('matchQueue.waitingTitle', null, '매칭 결과를 기다리고 있습니다.')}</div>
            <div style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(235,246,255,.78)', marginBottom: 18 }}>
              {t('matchQueue.waitingDescription', null, '성공 또는 실패 결과가 확인되는 즉시 안내됩니다.')}
            </div>
            {estimatedWait ? (
              <div style={{
                marginBottom: 10,
                fontSize: 14,
                fontWeight: 800,
                color: '#9ee9ff',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {t('matchQueue.estimatedWait', {
                  low: formatRemaining(estimatedWait.lowMs),
                  high: formatRemaining(estimatedWait.highMs),
                }, '예상 대기 약 {low}~{high}')}
              </div>
            ) : null}
            <div style={{
              display: 'inline-flex',
              minWidth: 180,
              justifyContent: 'center',
              padding: '12px 18px',
              borderRadius: 12,
              background: 'rgba(83, 199, 255, .10)',
              border: '1px solid rgba(83, 199, 255, .26)',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 21,
              fontWeight: 900,
            }}>
              {t('matchQueue.maxRemaining', { time: remainingText }, '최대 남은 시간 {time}')}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 19, fontWeight: 900, lineHeight: 1.45, marginBottom: 9 }}>
              {t('matchQueue.longWaitTitle', null, '대기열 계산 후 최대 10분 걸릴 수도 있습니다.')}
            </div>
            <div style={{ fontSize: 15, color: 'rgba(235,246,255,.82)', marginBottom: 22 }}>
              {t('matchQueue.continueQuestion', null, '계속 대기하시겠습니까?')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                type="button"
                disabled={busy}
                onClick={handleCancel}
                style={{
                  height: 44,
                  borderRadius: 11,
                  border: '1px solid rgba(255,255,255,.18)',
                  background: 'rgba(255,255,255,.07)',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? .6 : 1,
                }}
              >
                {t('common.no', null, '아니오')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={handleContinue}
                style={{
                  height: 44,
                  borderRadius: 11,
                  border: '1px solid rgba(99,219,255,.55)',
                  background: 'linear-gradient(180deg, rgba(55,195,255,.92), rgba(19,128,218,.92))',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 900,
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? .6 : 1,
                  boxShadow: '0 8px 22px rgba(29,147,223,.25)',
                }}
              >
                {t('common.yes', null, '예')}
              </button>
            </div>
          </>
        )}

        {error ? <div style={{ marginTop: 12, color: '#ff9f9f', fontSize: 12, lineHeight: 1.45 }}>{error}</div> : null}
      </div>
    </div>
  );
}
